import React from 'react';
import Link from 'next/link';
import { Container } from '../../components/common';
import { CardDescriptionHtml } from '@/components/visual-cms/CardDescriptionHtml';

export interface HeroSectionProps {
  title?: string;
  titleHighlight?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  imageUrl?: string;
}

const HERO_DEFAULTS = {
  title: 'Rent what you need.',
  titleHighlight: 'Share what you own.',
  description:
    'A trusted community marketplace where people can rent what they need and share what they own. Simple, secure, and local.',
  ctaLabel: 'Start Renting',
  ctaHref: '/browse',
  secondaryCtaLabel: 'List Your Item',
  secondaryCtaHref: '/listings/create',
  imageUrl:
    'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?auto=format&fit=crop&w=1400&q=80',
};

export const HeroSection: React.FC<HeroSectionProps> = (props) => {
  const title = props.title?.trim() || HERO_DEFAULTS.title;
  const titleHighlight = props.titleHighlight?.trim() ?? HERO_DEFAULTS.titleHighlight;
  const description = props.description?.trim() || HERO_DEFAULTS.description;
  const ctaLabel = props.ctaLabel?.trim() || HERO_DEFAULTS.ctaLabel;
  const ctaHref = props.ctaHref?.trim() || HERO_DEFAULTS.ctaHref;
  const secondaryCtaLabel = props.secondaryCtaLabel?.trim() || HERO_DEFAULTS.secondaryCtaLabel;
  const secondaryCtaHref = props.secondaryCtaHref?.trim() || HERO_DEFAULTS.secondaryCtaHref;
  const imageUrl = props.imageUrl?.trim() || HERO_DEFAULTS.imageUrl;

  return (
    <section className="relative bg-v2-neutral-0 overflow-hidden">
      {/* Right-side full-bleed image */}
      <div
        className="hidden lg:block absolute inset-y-0 right-0 w-[55%] bg-cover bg-center"
        style={{ backgroundImage: `url(${imageUrl})` }}
        aria-hidden
      >
        {/* Left fade so the text never overlaps the image */}
        <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-v2-neutral-0 via-v2-neutral-0/80 to-transparent" />
      </div>

      {/* Mobile inline image */}
      <div
        className="lg:hidden h-[260px] bg-cover bg-center"
        style={{ backgroundImage: `url(${imageUrl})` }}
        aria-hidden
      />

      <Container className="relative py-12 lg:py-20">
        <div className="max-w-[560px]">
          <h1 className="fontFamily-display text-[40px] sm:text-[48px] lg:text-[56px] font-bold tracking-[-0.02em] leading-[1.05] text-v2-neutral-900 mb-4">
            {title}
            {titleHighlight ? (
              <>
                <br />
                <span className="text-v2-green-500">{titleHighlight}</span>
              </>
            ) : null}
          </h1>
          <CardDescriptionHtml
            as="p"
            text={description}
            className="text-[15px] lg:text-[18px] text-v2-neutral-500 leading-[1.65] mb-8 max-w-md"
          />
          <div className="flex flex-wrap gap-3 mb-10">
            <Link
              href={ctaHref}
              className="inline-flex items-center gap-2 px-7 py-[14px] bg-v2-green-500 text-white font-semibold text-[15px] rounded-v2-full hover:bg-v2-green-600 transition-colors shadow-sm"
            >
              {ctaLabel}
            </Link>
            <Link
              href={secondaryCtaHref}
              className="inline-flex items-center gap-2 px-[27px] py-[13px] bg-v2-neutral-0 border-[1.5px] border-v2-green-400 text-v2-green-500 font-semibold text-[15px] rounded-v2-full hover:bg-v2-green-50 transition-colors"
            >
              {secondaryCtaLabel}
            </Link>
          </div>
        </div>
      </Container>
    </section>
  );
};
